import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router";

import { useProject } from "../ProjectProvider";
import { useAssetsCollection } from "../../../hooks/capture/useAssetsCollection";
import useAssetsFilter from "../../../hooks/capture/useAssetsFilter";
import useMutation from "../../../hooks/useMutation";
import { CREATE_GROUPED_ASSET_MUTATION } from "../../../constants/graphql/mutations";
import GroupedAssetDetailsModal from "../../TablePro/components/assets/GroupedAssetDetailsModal";
import { GroupedAssetsCollectionProvider } from "../../../contexts/groupedAssetsCollection";
import { GroupedAssetProvider } from "../../../contexts/grouped-asset";
import SearchGroupedAssets from "./SearchGroupedAssets";
import MoreOptionsMenu from "./MoreOptionsMenu";
import useQueryParams from "../../../hooks/useQueryParams";

export default function GroupedItems({ title }) {
  const { project } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const [selected, setSelected] = useState(null);
  const selectedGroupedAssetId = queryParams.get("groupedAssetId");
  const baseVariables = {
    projectId: project?.id,
    order: { item_id: "desc" },
  };
  const { filter, search, setSearch, limit } = useAssetsFilter(baseVariables, "GroupedAsset");
  const [{ data, loading, refetch }, { onLoadMore, fetchTotalIds }] = useAssetsCollection(filter);
  const [createGroupedAsset, { loading: creating }] = useMutation(CREATE_GROUPED_ASSET_MUTATION);
  const collection = data?.assetsCollection?.collection || [];

  const updateModalParams = assetId => {
    const params = new URLSearchParams(location.search);

    if (assetId) {
      params.set("groupedAssetId", String(assetId));
    } else {
      params.delete("groupedAssetId");
      params.delete("showGroupedQuantityCalculation");
    }

    navigate({ search: params.toString() }, { replace: true });
  };

  const handleSelect = asset => {
    setSelected(asset);
    updateModalParams(asset?.id);
  };

  const handleCloseSelected = () => {
    setSelected(null);
    updateModalParams(null);
  };

  const handleAddNewGroupedAsset = async () => {
    if (!project?.id) return;

    return createGroupedAsset({
      variables: { projectId: project.id, input: {} },
      onSuccess: async ({ groupedAsset }) => {
        await refetch({ fetchPolicy: "network-only" });
        handleSelect(groupedAsset);
      },
    });
  };

  const queryVariables = { page: 1, limit, projectId: project?.id };
  const selectedEntity = useMemo(() => {
    if (selected?.id) return selected;
    if (!selectedGroupedAssetId) return null;

    const matchingGroupedAsset = collection.find(
      asset => String(asset.id) === String(selectedGroupedAssetId)
    );

    return matchingGroupedAsset || { id: selectedGroupedAssetId };
  }, [collection, selected, selectedGroupedAssetId]);

  return (
    <GroupedAssetsCollectionProvider variables={queryVariables}>
      <Paper sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {title}
        <Box px={3} pb={2} pt={1} display="flex" flexDirection="column" flex={1} minHeight={0}>
          <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
            <Box display="flex" justifyContent="center" py={1}>
              <Box
                component="img"
                borderRadius={3}
                maxWidth={140}
                maxHeight={110}
                src="/kitting-graphic.webp"
              />
            </Box>
            {loading && !data ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : !search && !loading && collection.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Typography align="center" mb={1} maxWidth={450}>
                  Use this to group items by SKU Number. This gives you an aggregate available quantity of all items 
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewGroupedAsset}
                  disabled={creating}
                  sx={{ my: 2 }}
                >
                  Add New Grouped Item
                </Button>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="center">
                  <Typography align="center" variant="body1" noWrap>
                    If you'd like to add more grouped items, use the "More Options" button
                  </Typography>
                </Box>
                <SearchGroupedAssets
                  project={project}
                  data={data}
                  loading={loading || creating}
                  onLoadMore={onLoadMore}
                  search={search}
                  setSearch={setSearch}
                  limit={limit}
                  setSelected={handleSelect}
                  refetch={refetch}
                  fetchTotalIds={fetchTotalIds}
                  headerActions={
                    <MoreOptionsMenu
                      onAddNew={handleAddNewGroupedAsset}
                      creating={loading || creating}
                    />
                  }
                />
              </>
            )}
          </Box>
        </Box>
        {selectedEntity && (
          <GroupedAssetProvider id={selectedEntity.id}>
            <GroupedAssetDetailsModal
              projectId={project?.id}
              entity={selectedEntity}
              onClose={handleCloseSelected}
              refetch={refetch}
            />
          </GroupedAssetProvider>
        )}
      </Paper>
    </GroupedAssetsCollectionProvider>
  );
}
